import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { API_URL } from "../config";
import "./Documents.css";
import PDFViewer from "../components/PDFViewer";

const Documents = () => {
  const { user } = useAuth();
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const fileInputRefs = useRef({}); // Add refs for file inputs
  const [viewingPdf, setViewingPdf] = useState(null);

  // Mock data for dispatcher's carriers - this will come from your database later
  const mockCarriers = [
    { _id: "1", name: "ABC Trucking", mc: "MC123456", status: "complete" },
    { _id: "2", name: "XYZ Transport", mc: "MC789012", status: "incomplete" },
    { _id: "3", name: "Fast Freight", mc: "MC345678", status: "pending" },
  ];

  // Track status of each document
  const [documents, setDocuments] = useState({
    // Pre-filled forms
    carrierSetup: { status: "missing", file: null },
    carrierBrokerAgreement: { status: "missing", file: null },
    dispatchAgreement: { status: "missing", file: null },

    // Required uploads
    w9Form: { status: "missing", file: null },
    mcAuthority: { status: "missing", file: null },
    insuranceCOI: { status: "missing", file: null },
    workersComp: { status: "optional", file: null },
    voidedCheck: { status: "optional", file: null },
    factoringNOA: { status: "optional", file: null },
  });

  // Initialize uploadedFiles state
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [uploadError, setUploadError] = useState(null);

  // Load uploaded files on component mount
  useEffect(() => {
    if (selectedCarrier) {
      loadUploadedFiles(selectedCarrier._id);
    } else {
      loadUserFiles();
    }
  }, [selectedCarrier]);

  const loadUserFiles = async () => {
    try {
      const response = await axios.get(`${API_URL}/upload/files/user`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      // Initialize operational documents
      const newUploadedFiles = {};

      // Process files by type
      response.data.forEach((file) => {
        if (
          file.documentType === "rateConfirmation" ||
          file.documentType === "billOfLading"
        ) {
          // Add to operational documents array
          newUploadedFiles[file.documentType] = file;
        } else {
          // Handle regular documents
          newUploadedFiles[file.documentType] = file;

          // Update documents state for regular documents
          setDocuments((prev) => ({
            ...prev,
            [file.documentType]: {
              status: "uploaded",
              file: { name: file.originalName },
            },
          }));
        }
      });

      setUploadedFiles(newUploadedFiles);
      setUploadError(null);
    } catch (error) {
      console.error("Error loading files:", error);
      setUploadError("Failed to load uploaded files");
    }
  };

  const loadUploadedFiles = async (carrierId) => {
    try {
      const response = await axios.get(
        `${API_URL}/upload/files/carrier/${carrierId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      );

      // Organize files by document type
      const files = response.data.reduce((acc, file) => {
        if (
          file.documentType === "rateConfirmations" ||
          file.documentType === "billsOfLading"
        ) {
          // For operational documents, group files in an array
          acc[file.documentType] = {
            files: [...(acc[file.documentType]?.files || []), file],
          };
        } else {
          // For regular documents, store single file
          acc[file.documentType] = file;
        }
        return acc;
      }, {});

      setUploadedFiles(files);

      // Update documents state for regular documents
      Object.entries(files).forEach(([docType, file]) => {
        if (
          docType !== "rateConfirmations" &&
          docType !== "billsOfLading" &&
          file
        ) {
          setDocuments((prev) => ({
            ...prev,
            [docType]: {
              status: "uploaded",
              file: { name: file.originalName },
            },
          }));
        }
      });

      setUploadError(null);
    } catch (error) {
      console.error("Error loading files:", error);
      setUploadError("Failed to load uploaded files");
    }
  };

  const preFilledForms = [
    {
      id: "carrierSetup",
      label: "Carrier Setup Form",
      icon: "fa-file-contract",
      required: true,
    },
    {
      id: "carrierBrokerAgreement",
      label: "Carrier-Broker Agreement",
      icon: "fa-file-signature",
      required: true,
    },
    {
      id: "dispatchAgreement",
      label: "Dispatch Agreement",
      icon: "fa-handshake",
      required: true,
    },
  ];

  const documentTypes = [
    { id: "w9Form", label: "W-9 Form", required: true },
    { id: "mcAuthority", label: "MC Authority", required: true },
    {
      id: "insuranceCOI",
      label: "Certificate of Insurance (Cargo + Liability)",
      required: true,
    },
    { id: "workersComp", label: "Workers Comp Policy", required: false },
    { id: "voidedCheck", label: "Voided Invoice or Check", required: false },
    {
      id: "factoringNOA",
      label: "Factoring Notice of Assignment",
      required: false,
    },
  ];

  const handleFileUpload = async (event, docType) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== "application/pdf") {
      setUploadError("Only PDF files are allowed");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("File size must be less than 5MB");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);

      // Add metadata
      if (selectedCarrier?._id) {
        formData.append("carrierId", selectedCarrier._id);
      }
      formData.append("documentType", docType);

      const response = await axios.post(`${API_URL}/upload/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      // Update documents state
      setDocuments((prev) => ({
        ...prev,
        [docType]: {
          status: "uploaded",
          file: file,
        },
      }));

      // Update uploadedFiles state
      setUploadedFiles((prev) => ({
        ...prev,
        [docType]: response.data,
      }));

      setUploadError(null);
    } catch (error) {
      console.error("Upload error:", error);
      setUploadError("Failed to upload file. Please try again.");
    }
  };

  const handleDelete = async (docType) => {
    try {
      const fileId = uploadedFiles[docType]?._id;
      if (!fileId) return;

      await axios.delete(`${API_URL}/upload/files/${fileId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      // Update local state
      setDocuments((prev) => ({
        ...prev,
        [docType]: {
          status: "missing",
          file: null,
        },
      }));

      setUploadedFiles((prev) => {
        const newState = { ...prev };
        delete newState[docType];
        return newState;
      });
    } catch (error) {
      console.error("Error deleting file:", error);
      setUploadError("Failed to delete file");
    }
  };

  const handleReplace = async (docType) => {
    // First delete the existing file
    await handleDelete(docType);

    // Then trigger new file upload
    if (!fileInputRefs.current[docType]) {
      fileInputRefs.current[docType] = document.createElement("input");
      fileInputRefs.current[docType].type = "file";
      fileInputRefs.current[docType].accept = ".pdf";
      fileInputRefs.current[docType].onchange = (e) =>
        handleFileUpload(e, docType);
    }

    fileInputRefs.current[docType].click();
  };

  const handleViewDocument = async (docType) => {
    try {
      const fileId = uploadedFiles[docType]?._id;
      if (!fileId) return;

      const response = await axios.get(`${API_URL}/upload/files/${fileId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      setViewingPdf(response.data.presignedUrl);
    } catch (error) {
      console.error("Error viewing file:", error);
      setUploadError("Failed to view file");
    }
  };

  const renderDocumentCard = (doc) => (
    <div key={doc.id} className="document-card">
      <div className="document-icon">
        <i className={`fas ${doc.icon || "fa-file-upload"}`}></i>
      </div>
      <div className="document-info">
        <h3>{doc.label}</h3>
        <p>Status: {documents[doc.id].status}</p>
        {uploadedFiles[doc.id] ? (
          <div className="file-info">
            <p>{uploadedFiles[doc.id].originalName}</p>
            <div className="button-group">
              <button
                onClick={() => handleViewDocument(doc.id)}
                className="view-btn"
              >
                View
              </button>
              <button
                onClick={() => handleReplace(doc.id)}
                className="replace-btn"
              >
                Replace
              </button>
              <button
                onClick={() => handleDelete(doc.id)}
                className="delete-btn"
              >
                Delete
              </button>
            </div>
          </div>
        ) : (
          <div className="upload-area">
            <input
              type="file"
              id={`file-${doc.id}`}
              onChange={(e) => handleFileUpload(e, doc.id)}
              accept=".pdf"
              style={{ display: "none" }}
            />
            <label htmlFor={`file-${doc.id}`} className="upload-btn">
              Upload {doc.required && "*"}
            </label>
          </div>
        )}
      </div>
    </div>
  );

  // Calculate progress only for carrier package documents
  const calculateProgress = () => {
    const carrierDocs = [
      "carrierSetup",
      "carrierBrokerAgreement",
      "dispatchAgreement",
      "w9Form",
      "mcAuthority",
      "insuranceCOI",
      "workersComp",
      "voidedCheck",
      "factoringNOA",
    ];

    const completedDocs = carrierDocs.filter(
      (docId) =>
        documents[docId].status === "uploaded" ||
        documents[docId].status === "completed",
    ).length;

    return (completedDocs / carrierDocs.length) * 100;
  };

  const renderCarrierPackage = () => (
    <>
      <div className="documents-section">
        <h2>Pre-filled Forms</h2>
        <p className="section-description">
          Upload your completed forms (Auto-fill feature coming soon)
        </p>
        <div className="forms-grid">
          {preFilledForms.map((form) => renderDocumentCard(form))}
        </div>
      </div>

      <div className="documents-section">
        <h2>Required Documents</h2>
        <div className="uploads-grid">
          {documentTypes.map((doc) => renderDocumentCard(doc))}
        </div>
      </div>

      <div className="package-status">
        <h3>Carrier Package Status</h3>
        <div className="status-bar">
          <div
            className="status-progress"
            style={{ width: `${calculateProgress()}%` }}
          ></div>
        </div>
        <p>
          Complete all required documents (*) to activate your carrier package
        </p>
      </div>
    </>
  );

  const renderDispatcherView = () => (
    <>
      <div className="documents-section">
        <h2>Carrier Documents</h2>
        <p className="section-description">
          View and manage your carriers' document packages
        </p>
        <div className="carriers-grid">
          {mockCarriers.map((carrier) => (
            <div key={carrier._id} className="carrier-card">
              <div className="carrier-info">
                <h3>{carrier.name}</h3>
                <p>MC#: {carrier.mc}</p>
                <div className={`status-badge ${carrier.status}`}>
                  {carrier.status.charAt(0).toUpperCase() +
                    carrier.status.slice(1)}
                </div>
              </div>
              <button
                className="view-docs-btn"
                onClick={() => setSelectedCarrier(carrier)}
              >
                View Documents
              </button>
            </div>
          ))}
        </div>
      </div>

      {selectedCarrier && (
        <div className="carrier-documents">
          <div className="carrier-documents-header">
            <h3>{selectedCarrier.name} - Documents</h3>
            <button
              className="close-btn"
              onClick={() => setSelectedCarrier(null)}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          {renderCarrierPackage()}
        </div>
      )}
    </>
  );

  return (
    <div className="documents-container">
      {uploadError && <div className="error-message">{uploadError}</div>}

      {/* Show different views based on account type */}
      {user.account_type === "carrier"
        ? renderCarrierPackage()
        : renderDispatcherView()}

      {viewingPdf && (
        <PDFViewer url={viewingPdf} onClose={() => setViewingPdf(null)} />
      )}
    </div>
  );
};

export default Documents;
